import React, { useRef, useState } from "react";
import type { Theme } from "@material-ui/core";
import { Button, Menu, makeStyles, createStyles } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faPlus, faUsers, faWarehouseAlt } from "@fortawesome/pro-light-svg-icons";
import { useTranslation } from "react-i18next";

import type { IResponse, IPartnerSlim } from "App/Modules/Models";
import { Permission } from "App/Modules/Models";
import type {
    ISitesRepository,
    ICompaniesRepository,
    IPartnersRepository,
    ITeamsRepository,
} from "App/Modules/Repositories";

import { MenuItemWithIcon, getUserProfile, hasUserPermissionOnAnyPartner } from "App/ReactComponents/Shared";
import type { CreateType, CreatedEntityWithId, CreateEntityByName } from "./CreateAnythingDialog";
import { CreateAnythingDialog } from "./CreateAnythingDialog";

type InjectedProps = {
    mmCompaniesRepository: ICompaniesRepository;
    mmSitesRepository: ISitesRepository;
    mmTeamsRepository: ITeamsRepository;
    mmPartnersRepository: IPartnersRepository;
};

type CreateAnythigButtonProps = InjectedProps;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        refreshButton: {
            alignSelf: "flex-start",
            marginBottom: theme.spacing(1),
        },
    }),
);

export const CreateAnythingButton = (props: CreateAnythigButtonProps) => {
    const { t } = useTranslation();
    const menuButtonRef = useRef();
    const [menuOpen, setMenuOpen] = useState(false);

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [createType, setCreateType] = useState<CreateType>();

    const profile = getUserProfile();
    const hasAccessToButton = hasUserPermissionOnAnyPartner(profile, Permission.PartnerOperations);

    const classes = useStyles();

    const closeMenu = () => {
        setMenuOpen(false);
    };
    const openMenu = () => {
        setMenuOpen(true);
    };

    const handleCreateOptionClicked = (typeToCreate: CreateType) => {
        setCreateType(typeToCreate);
        setAddDialogOpen(true);
        setMenuOpen(false);
    };

    const handleDialogClose = (typeToCreate: CreateType, createdEntityResponse: IResponse<CreatedEntityWithId>) => {
        setAddDialogOpen(false);
        if (createdEntityResponse?.data?.id) {
            switch (createType) {
                case "Company":
                    window.location.hash = `#!/companies/${createdEntityResponse.data.id}`;
                    break;
                case "Site":
                    window.location.hash = `#!/sites/${createdEntityResponse.data.id}`;
                    break;
                case "Team":
                    window.location.hash = `#!/teams/${createdEntityResponse.data.id}`;
                    break;
                default:
                    throw new Error(`Type to create ${typeToCreate as any} not supported`);
            }
        }
    };

    const handleDialogSubmit = (typeToCreate: CreateType, entity: CreateEntityByName) => {
        const { partnerId, ...otherEntityProps } = entity;
        switch (typeToCreate) {
            case "Company":
                return props.mmCompaniesRepository.addCompany({
                    ...otherEntityProps,
                    partner: { id: partnerId } as IPartnerSlim,
                });
            case "Site":
                return props.mmSitesRepository.addSite({ ...entity });
            case "Team":
                return props.mmTeamsRepository.addTeam({ ...entity });
            default:
                throw new Error(`Type to create ${typeToCreate as any} not supported`);
        }
    };

    return hasAccessToButton ? (
        <>
            <Button
                classes={{ root: classes.refreshButton }}
                variant="contained"
                size="large"
                startIcon={<FontAwesomeIcon icon={faPlus} fixedWidth />}
                onClick={() => openMenu()}
                ref={menuButtonRef}
            >
                {t("Gui.Buttons.AddAnything")}
            </Button>
            <Menu
                open={menuOpen}
                onClose={() => closeMenu()}
                anchorEl={menuButtonRef.current}
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <MenuItemWithIcon onClick={() => handleCreateOptionClicked("Site")} icon={faWarehouseAlt}>
                    {t("Entities.Site.Site")}
                </MenuItemWithIcon>
                <MenuItemWithIcon onClick={() => handleCreateOptionClicked("Company")} icon={faBuilding}>
                    {t("Entities.Company.Company")}
                </MenuItemWithIcon>
                <MenuItemWithIcon onClick={() => handleCreateOptionClicked("Team")} icon={faUsers}>
                    {t("Entities.Team.Team")}
                </MenuItemWithIcon>
            </Menu>
            <CreateAnythingDialog
                typeToCreate={createType}
                open={addDialogOpen}
                onClose={handleDialogClose}
                onSubmit={handleDialogSubmit}
                mmPartnersRepository={props.mmPartnersRepository}
            />
        </>
    ) : (
        <></>
    );
};
